import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import JSZip from "jszip"
import { saveAs } from "file-saver"
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Avatar,
  Chip,
  LinearProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
} from "@mui/material"
import BackupIcon from "@mui/icons-material/Backup"
import RestoreIcon from "@mui/icons-material/Restore"
import CloudDownloadIcon from "@mui/icons-material/CloudDownload"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import StorageIcon from "@mui/icons-material/Storage"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import TableChartIcon from "@mui/icons-material/TableChart"
import MuiPageHeader from "../../components/mui/MuiPageHeader"
import { fetchPaginated } from "../../helpers/api_helper"
import config from "../../config/app.config"

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

// Get auth headers
const getAuthHeaders = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser"))
  const token = authUser ? authUser.session_token : ""
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
    "X-Api-Key": config.APP_API_KEY,
  }
}

// Generic POST to any table via _table API
const postToTable = async (tableName, records) => {
  const url = `${config.APP_URL}_table/${tableName}`
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ resource: records }),
  })
  if (!res.ok) {
    const err = await res.text()
    throw new Error(`POST ${tableName} failed (${res.status}): ${err}`)
  }
  return res.json()
}

// Delete all records from a table via _table API
const deleteAllFromTable = async (tableName) => {
  const url = `${config.APP_URL}_table/${tableName}?filter=(id>0)`
  const res = await fetch(url, {
    method: "DELETE",
    headers: getAuthHeaders(),
  })
  if (!res.ok) {
    const err = await res.text()
    throw new Error(`DELETE ${tableName} failed (${res.status}): ${err}`)
  }
  return res.json()
}

// Fetch list of all tables
const fetchTableList = async () => {
  const url = `${config.APP_URL}_table`
  const res = await fetch(url, {
    headers: { Accept: "application/json", "X-Api-Key": config.APP_API_KEY },
  })
  const data = await res.json()
  return (data.resource || []).map(t => t.name).sort()
}

const MuiSettingsBackup = () => {
  const { t } = useTranslation()

  document.title = t("Backup & Restore") + " | FlexFlow ERP"

  const [activeTab, setActiveTab] = useState(0)
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })
  const [tables, setTables] = useState([])
  const [tablesLoading, setTablesLoading] = useState(false)

  // Backup state
  const [backupInProgress, setBackupInProgress] = useState(false)
  const [backupProgress, setBackupProgress] = useState(0)
  const [backupStatus, setBackupStatus] = useState("")

  // Export state
  const [selectedExportTable, setSelectedExportTable] = useState("")
  const [exportInProgress, setExportInProgress] = useState(false)

  // Restore state
  const [restoreInProgress, setRestoreInProgress] = useState(false)
  const [restoreProgress, setRestoreProgress] = useState(0)
  const [restoreStatus, setRestoreStatus] = useState("")
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false)
  const [parsedBackup, setParsedBackup] = useState(null)
  const [selectedRestoreTypes, setSelectedRestoreTypes] = useState({})
  const [replaceExisting, setReplaceExisting] = useState(false)
  const [restoreFileType, setRestoreFileType] = useState("") // "zip" or "json"

  // Load tables on mount
  useEffect(() => {
    setTablesLoading(true)
    fetchTableList()
      .then(list => setTables(list))
      .catch(err => console.error("Failed to fetch tables:", err))
      .finally(() => setTablesLoading(false))
  }, [])

  // Full database backup
  const handleFullBackup = async () => {
    setBackupInProgress(true)
    setBackupProgress(0)
    setBackupStatus(t("Fetching table list..."))

    try {
      const tableList = tables.length > 0 ? tables : await fetchTableList()

      if (tableList.length === 0) {
        setSnackbar({ open: true, message: t("No tables found"), severity: "warning" })
        setBackupInProgress(false)
        return
      }

      setBackupProgress(10)

      const zip = new JSZip()
      const progressPerTable = 80 / tableList.length
      let currentProgress = 10
      const manifest = { app: config.APP_NAME, date: new Date().toISOString(), tables: {} }

      for (const table of tableList) {
        setBackupStatus(`${t("Exporting")} ${table}...`)
        try {
          const result = await fetchPaginated(`${config.APP_URL}_table/${table}`, {
            limit: 10000,
            offset: 0,
          })
          const records = result.data || []
          zip.file(`${table}.json`, JSON.stringify(records, null, 2))
          manifest.tables[table] = records.length
        } catch (err) {
          console.warn(`Skipping table ${table}:`, err.message)
          manifest.tables[table] = -1
        }
        currentProgress += progressPerTable
        setBackupProgress(Math.round(currentProgress))
      }

      zip.file("_manifest.json", JSON.stringify(manifest, null, 2))

      setBackupStatus(t("Generating ZIP..."))
      setBackupProgress(95)
      const content = await zip.generateAsync({ type: "blob" })
      const dateStr = new Date().toISOString().slice(0, 10)
      saveAs(content, `${config.APP_NAME}-backup-${dateStr}.zip`)

      setBackupProgress(100)
      setBackupStatus("")
      const totalRecords = Object.values(manifest.tables).filter(c => c > 0).reduce((a, b) => a + b, 0)
      setSnackbar({
        open: true,
        message: `${t("Backup complete")} — ${tableList.length} ${t("tables")}, ${totalRecords.toLocaleString()} ${t("records")}`,
        severity: "success",
      })
    } catch (error) {
      console.error("Backup error:", error)
      setSnackbar({ open: true, message: t("Backup failed"), severity: "error" })
    }

    setTimeout(() => {
      setBackupInProgress(false)
      setBackupProgress(0)
      setBackupStatus("")
    }, 1500)
  }

  // Single table export as JSON
  const handleExportTable = async () => {
    if (!selectedExportTable) return

    setExportInProgress(true)
    try {
      const result = await fetchPaginated(`${config.APP_URL}_table/${selectedExportTable}`, {
        limit: 10000,
        offset: 0,
      })
      const records = result.data || []

      const blob = new Blob(
        [JSON.stringify(records, null, 2)],
        { type: "application/json" }
      )
      const dateStr = new Date().toISOString().slice(0, 10)
      saveAs(blob, `${selectedExportTable}-${dateStr}.json`)

      setSnackbar({
        open: true,
        message: `${t("Exported")} ${selectedExportTable} — ${records.length} ${t("records")}`,
        severity: "success",
      })
    } catch (error) {
      console.error("Export error:", error)
      setSnackbar({ open: true, message: `${t("Export failed")}: ${error.message}`, severity: "error" })
    }

    setExportInProgress(false)
  }

  // Parse restore file — auto-detect ZIP (full backup) or JSON (single table)
  const handleFileSelect = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    const isZip = file.name.endsWith(".zip")
    const isJson = file.name.endsWith(".json")

    if (!isZip && !isJson) {
      setSnackbar({ open: true, message: t("Unsupported file type. Use .zip or .json"), severity: "error" })
      event.target.value = ""
      return
    }

    try {
      if (isZip) {
        // Full backup ZIP
        setRestoreFileType("zip")
        const zip = new JSZip()
        const content = await zip.loadAsync(file)

        const parsed = {}
        for (const filename of Object.keys(content.files)) {
          if (filename.startsWith("_") || !filename.endsWith(".json")) continue
          const tableName = filename.replace(".json", "")
          try {
            const raw = await content.files[filename].async("string")
            const records = JSON.parse(raw)
            parsed[tableName] = {
              available: true,
              count: Array.isArray(records) ? records.length : 0,
              data: records,
            }
          } catch {
            parsed[tableName] = { available: false, count: 0, data: [] }
          }
        }

        if (Object.keys(parsed).length === 0) {
          setSnackbar({ open: true, message: t("No valid data found in file"), severity: "error" })
          event.target.value = ""
          return
        }

        const initialSelection = {}
        for (const table of Object.keys(parsed)) {
          initialSelection[table] = parsed[table].available && parsed[table].count > 0
        }

        setParsedBackup(parsed)
        setSelectedRestoreTypes(initialSelection)
        setRestoreDialogOpen(true)
      } else {
        // Single table JSON
        setRestoreFileType("json")
        const raw = await file.text()
        const records = JSON.parse(raw)

        if (!Array.isArray(records) || records.length === 0) {
          setSnackbar({ open: true, message: t("JSON file is empty or not an array"), severity: "error" })
          event.target.value = ""
          return
        }

        // Detect table name from filename: "products-2026-03-17.json" → "products"
        const baseName = file.name.replace(".json", "")
        // Strip date suffix like "-2026-03-17"
        const tableName = baseName.replace(/-\d{4}-\d{2}-\d{2}$/, "")

        const parsed = {
          [tableName]: {
            available: true,
            count: records.length,
            data: records,
          },
        }

        setParsedBackup(parsed)
        setSelectedRestoreTypes({ [tableName]: true })
        setRestoreDialogOpen(true)
      }
    } catch (error) {
      console.error("Error parsing file:", error)
      setSnackbar({ open: true, message: t("Invalid file format"), severity: "error" })
    }

    event.target.value = ""
  }

  const handleToggleRestoreType = (type) => {
    if (!parsedBackup?.[type]?.available) return
    setSelectedRestoreTypes(prev => ({ ...prev, [type]: !prev[type] }))
  }

  const hasSelectedRestoreTypes = Object.entries(selectedRestoreTypes).some(
    ([type, selected]) => selected && parsedBackup?.[type]?.available
  )

  const handleCloseRestoreDialog = () => {
    setRestoreDialogOpen(false)
    setParsedBackup(null)
    setSelectedRestoreTypes({})
    setReplaceExisting(false)
    setRestoreFileType("")
  }

  const handleRestore = async () => {
    if (!hasSelectedRestoreTypes || !parsedBackup) return

    setRestoreDialogOpen(false)
    setRestoreInProgress(true)
    setRestoreProgress(0)
    setRestoreStatus("")

    try {
      const selectedTables = Object.entries(selectedRestoreTypes)
        .filter(([type, selected]) => selected && parsedBackup[type]?.available)
        .map(([type]) => type)

      const progressPerTable = 90 / selectedTables.length
      let currentProgress = 5
      let totalRestored = 0
      let failedTables = []
      setRestoreProgress(5)

      for (const table of selectedTables) {
        const records = parsedBackup[table].data
        if (!records || records.length === 0) {
          currentProgress += progressPerTable
          setRestoreProgress(Math.round(currentProgress))
          continue
        }

        setRestoreStatus(`${t("Restoring")} ${table} (${records.length} ${t("records")})...`)

        try {
          if (replaceExisting) {
            setRestoreStatus(`${t("Clearing")} ${table}...`)
            try {
              await deleteAllFromTable(table)
            } catch (e) {
              console.warn(`Could not clear ${table}:`, e.message)
            }
            setRestoreStatus(`${t("Restoring")} ${table} (${records.length} ${t("records")})...`)
          }

          // Strip id fields to avoid conflicts
          const cleanRecords = records.map(r => {
            const { id, ...rest } = r
            return rest
          })

          // Batch insert in chunks of 200
          const BATCH_SIZE = 200
          for (let i = 0; i < cleanRecords.length; i += BATCH_SIZE) {
            const batch = cleanRecords.slice(i, i + BATCH_SIZE)
            await postToTable(table, batch)
          }

          totalRestored += records.length
        } catch (e) {
          console.error(`Failed to restore ${table}:`, e)
          failedTables.push(table)
        }

        currentProgress += progressPerTable
        setRestoreProgress(Math.round(currentProgress))
      }

      setRestoreProgress(100)
      setRestoreStatus("")

      if (failedTables.length > 0) {
        setSnackbar({
          open: true,
          message: `${t("Restore partially complete")} — ${totalRestored} ${t("records restored")}, ${failedTables.length} ${t("tables failed")}: ${failedTables.join(", ")}`,
          severity: "warning",
        })
      } else {
        setSnackbar({
          open: true,
          message: `${t("Restore complete")} — ${totalRestored.toLocaleString()} ${t("records restored across")} ${selectedTables.length} ${t("tables")}`,
          severity: "success",
        })
      }
    } catch (error) {
      console.error("Restore error:", error)
      setSnackbar({ open: true, message: `${t("Restore failed")}: ${error.message}`, severity: "error" })
    }

    setTimeout(() => {
      setRestoreInProgress(false)
      setRestoreProgress(0)
      setRestoreStatus("")
      setParsedBackup(null)
      setSelectedRestoreTypes({})
      setReplaceExisting(false)
      setRestoreFileType("")
    }, 1500)
  }

  const breadcrumbs = [
    { label: t("Settings"), path: "/settings" },
    { label: t("Backup & Restore") },
  ]

  return (
    <Box sx={{ py: 3, bgcolor: "#f9fafb", minHeight: "100vh" }}>
      <Container maxWidth="md">
        <MuiPageHeader breadcrumbs={breadcrumbs} />

        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb" }}>
          <Box sx={{ borderBottom: "1px solid #e5e7eb" }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                px: 2,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  minHeight: 56,
                },
                "& .Mui-selected": { color: "#3b82f6" },
                "& .MuiTabs-indicator": { bgcolor: "#3b82f6" },
              }}
            >
              <Tab icon={<BackupIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={t("Backup")} />
              <Tab icon={<FileDownloadIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={t("Export")} />
              <Tab icon={<RestoreIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={t("Restore")} />
            </Tabs>
          </Box>

          {/* Backup Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ px: 3, pb: 3 }}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e5e7eb",
                  transition: "all 0.2s ease",
                  "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.08)" },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                    <Avatar sx={{ width: 48, height: 48, bgcolor: "#dbeafe", color: "#3b82f6" }}>
                      <StorageIcon sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} color="#111827">
                        {t("Full Database Backup")}
                      </Typography>
                      <Typography variant="body2" color="#6b7280" sx={{ mt: 0.5 }}>
                        {t("Download a complete backup of all tables in the")} <strong>{config.APP_NAME}</strong> {t("database as a ZIP file")}
                      </Typography>
                    </Box>
                  </Stack>

                  {backupInProgress && (
                    <Box sx={{ mb: 3 }}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="body2" color="#6b7280">
                          {backupStatus}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="#3b82f6">
                          {backupProgress}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={backupProgress}
                        sx={{ borderRadius: 1, height: 8, bgcolor: "#e5e7eb", "& .MuiLinearProgress-bar": { bgcolor: "#3b82f6" } }}
                      />
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    startIcon={<CloudDownloadIcon />}
                    onClick={handleFullBackup}
                    disabled={backupInProgress}
                    fullWidth
                    size="large"
                    sx={{
                      bgcolor: "#3b82f6",
                      "&:hover": { bgcolor: "#2563eb" },
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "1rem",
                      py: 1.5,
                    }}
                  >
                    {backupInProgress ? t("Exporting...") : t("Download Full Backup")}
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </TabPanel>

          {/* Export Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ px: 3, pb: 3 }}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e5e7eb",
                  transition: "all 0.2s ease",
                  "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.08)" },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                    <Avatar sx={{ width: 48, height: 48, bgcolor: "#fef3c7", color: "#d97706" }}>
                      <TableChartIcon sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} color="#111827">
                        {t("Export Table")}
                      </Typography>
                      <Typography variant="body2" color="#6b7280" sx={{ mt: 0.5 }}>
                        {t("Select a table and download its data as a JSON file")}
                      </Typography>
                    </Box>
                  </Stack>

                  <TextField
                    select
                    fullWidth
                    label={t("Select Table")}
                    value={selectedExportTable}
                    onChange={(e) => setSelectedExportTable(e.target.value)}
                    disabled={tablesLoading || exportInProgress}
                    sx={{ mb: 2 }}
                    size="small"
                  >
                    {tablesLoading ? (
                      <MenuItem value="" disabled>{t("Loading tables...")}</MenuItem>
                    ) : (
                      tables.map(table => (
                        <MenuItem key={table} value={table}>{table}</MenuItem>
                      ))
                    )}
                  </TextField>

                  <Button
                    variant="contained"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportTable}
                    disabled={!selectedExportTable || exportInProgress}
                    fullWidth
                    size="large"
                    sx={{
                      bgcolor: "#d97706",
                      "&:hover": { bgcolor: "#b45309" },
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "1rem",
                      py: 1.5,
                    }}
                  >
                    {exportInProgress ? t("Exporting...") : t("Export as JSON")}
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </TabPanel>

          {/* Restore Tab */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ px: 3, pb: 3 }}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e5e7eb",
                  transition: "all 0.2s ease",
                  "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.08)" },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                    <Avatar sx={{ width: 48, height: 48, bgcolor: "#d1fae5", color: "#10b981" }}>
                      <RestoreIcon sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} color="#111827">
                        {t("Restore Data")}
                      </Typography>
                      <Typography variant="body2" color="#6b7280" sx={{ mt: 0.5 }}>
                        {t("Upload a backup file to restore data")}
                      </Typography>
                    </Box>
                  </Stack>

                  {restoreInProgress ? (
                    <Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="body2" color="#6b7280">
                          {restoreStatus || t("Restoring...")}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="#10b981">
                          {restoreProgress}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={restoreProgress}
                        sx={{ borderRadius: 1, height: 8, bgcolor: "#e5e7eb", "& .MuiLinearProgress-bar": { bgcolor: "#10b981" } }}
                      />
                    </Box>
                  ) : (
                    <Box
                      component="label"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 4,
                        border: "2px dashed #d1d5db",
                        borderRadius: 2,
                        bgcolor: "#f9fafb",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": { borderColor: "#10b981", bgcolor: "#f0fdf4" },
                      }}
                    >
                      <input type="file" accept=".zip,.json" onChange={handleFileSelect} style={{ display: "none" }} />
                      <CloudUploadIcon sx={{ fontSize: 40, color: "#9ca3af", mb: 1 }} />
                      <Typography variant="body2" fontWeight={500} color="#374151">
                        {t("Click to upload backup file")}
                      </Typography>
                      <Typography variant="caption" color="#6b7280">
                        {t(".zip (full backup) or .json (single table)")}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          </TabPanel>
        </Card>

        {/* Restore Dialog */}
        <Dialog open={restoreDialogOpen} onClose={handleCloseRestoreDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">{t("Restore Preview")}</Typography>
              <Chip
                label={restoreFileType === "zip" ? t("Full Backup") : t("Single Table")}
                size="small"
                color={restoreFileType === "zip" ? "primary" : "warning"}
                sx={{ height: 24, fontSize: "0.7rem" }}
              />
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="#6b7280" sx={{ mt: 2, mb: 2 }}>
              {restoreFileType === "json"
                ? t("Detected table from filename. You can change the target table name if needed:")
                : t("Select which tables to restore:")}
            </Typography>

            {/* For single JSON, allow editing the target table name */}
            {restoreFileType === "json" && parsedBackup && (
              <TextField
                select
                fullWidth
                label={t("Target Table")}
                value={Object.keys(selectedRestoreTypes)[0] || ""}
                onChange={(e) => {
                  const oldKey = Object.keys(parsedBackup)[0]
                  const newKey = e.target.value
                  if (oldKey === newKey) return
                  const data = parsedBackup[oldKey]
                  setParsedBackup({ [newKey]: data })
                  setSelectedRestoreTypes({ [newKey]: true })
                }}
                size="small"
                sx={{ mb: 2 }}
              >
                {tables.map(table => (
                  <MenuItem key={table} value={table}>{table}</MenuItem>
                ))}
              </TextField>
            )}

            <Stack spacing={0.5}>
              {parsedBackup && Object.keys(parsedBackup).sort().map((table) => {
                const data = parsedBackup[table]
                return (
                  <FormControlLabel
                    key={table}
                    control={
                      <Checkbox
                        checked={selectedRestoreTypes[table] || false}
                        onChange={() => handleToggleRestoreType(table)}
                        disabled={!data?.available || data?.count === 0}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontSize: "0.875rem" }}>{table}</Typography>
                        {data?.available && data?.count > 0 ? (
                          <Chip label={`${data.count} ${t("records")}`} size="small" color="primary" variant="outlined" sx={{ height: 22, fontSize: "0.7rem" }} />
                        ) : (
                          <Chip label={t("Empty")} size="small" color="default" variant="outlined" sx={{ height: 22, fontSize: "0.7rem" }} />
                        )}
                      </Stack>
                    }
                  />
                )
              })}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", justifyContent: "space-between" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={replaceExisting}
                  onChange={(e) => setReplaceExisting(e.target.checked)}
                  sx={{ color: "#ef4444", "&.Mui-checked": { color: "#ef4444" } }}
                />
              }
              label={
                <Typography variant="body2" color={replaceExisting ? "#ef4444" : "#6b7280"} fontWeight={replaceExisting ? 600 : 400}>
                  {t("Delete existing data first")}
                </Typography>
              }
            />
            <Stack direction="row" spacing={1}>
              <Button onClick={handleCloseRestoreDialog}>{t("Cancel")}</Button>
              <Button
                variant="contained"
                onClick={handleRestore}
                disabled={!hasSelectedRestoreTypes}
                sx={{ bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" } }}
              >
                {t("Restore Selected")}
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  )
}

export default MuiSettingsBackup
